import React, { Suspense, useEffect } from "react";

import { Loading, ToastContainer } from "@commonComponents";
import * as Sentry from "@sentry/react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import axios from "axios";
import Highcharts from "highcharts";
import queryString from "query-string";
import { createRoot } from "react-dom/client";
import "sweetalert2/src/sweetalert2.scss";
import "./index.css";
import "react-circular-progressbar/dist/styles.css";
import "reactflow/dist/style.css";
import { Provider } from "react-redux";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";
ModuleRegistry.registerModules([AllCommunityModule]);

const App = React.lazy(() => import("./App"));
import { cachedAxios } from "@utils";

import { AppContextProvider } from "./context";
import store from "./store";

// Fingerprint Sentry errors to group by endpoint and status
const fingerprintKnownErrors: NonNullable<
  Parameters<typeof Sentry.init>[0]
>["beforeSend"] = (event, hint) => {
  const error = hint?.originalException as
    | (Error & {
        isAxiosError?: boolean;
        code?: string;
        errorCode?: string;
        config?: { baseURL?: string; url?: string };
        response?: { status?: number };
      })
    | null
    | undefined;

  if (error && typeof error === "object") {
    if (error.isAxiosError) {
      const status = error.response?.status ?? error.code ?? "network";
      let host = "unknown-host";
      let path = "unknown-path";
      try {
        const url = new URL(
          error.config?.url ?? "",
          error.config?.baseURL ?? window.location.origin,
        );
        host = url.hostname;
        path = url.pathname
          .replace(
            /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
            ":uuid",
          )
          .replace(/\/\d+(?=\/|$)/g, "/:id");
      } catch {
        // keep the "unknown" defaults above
      }
      event.fingerprint = ["axios-error", host, path, String(status)];
    } else if (
      ["AuthApiError", "AuthSdkError", "OAuthError"].includes(error.name ?? "")
    ) {
      event.fingerprint = [
        error.name ?? "OktaError",
        error.errorCode ?? error.message ?? "unknown",
      ];
    }
  }

  return event;
};

if (window.location.hostname !== "localhost") {
  const environment = window.location.hostname.split(".").shift();

  Sentry.init({
    environment,
    dsn: "https://d638b6e88bf044eebc803b4a9851d1fa@o524965.ingest.sentry.io/5454563",
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.browserProfilingIntegration(),
      Sentry.captureConsoleIntegration({
        levels: ["error"],
      }),
      Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
      Sentry.launchDarklyIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
      }),
    ],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    profileSessionSampleRate: 0,
    tracesSampleRate: 0.1,
    normalizeDepth: 15,
    beforeSend: fingerprintKnownErrors,
  });
}

// Import font awesome kit - we do have local version of FA but this will always ensure we have the latest
(function () {
  const wa = document.createElement("script");
  wa.type = "text/javascript";
  wa.src = "https://kit.fontawesome.com/0870df4268.js";
  wa.crossOrigin = "anonymous";
  const s = document.getElementsByTagName("script")[0];
  s.parentNode?.insertBefore(wa, s);
})();

// We need to check if we are working locally or on a server, if host contains localhost we are working locally
if (window.location.hostname === "localhost") {
  axios.defaults.baseURL = `${window.location.origin}/api/v1/`;
  cachedAxios.defaults.baseURL = `${window.location.origin}/api/v1/`;
} else {
  axios.defaults.baseURL = `https://internal-api.${window.location.host}/api/v1/`;
  cachedAxios.defaults.baseURL = `https://internal-api.${window.location.host}/api/v1/`;
}
axios.defaults.paramsSerializer = (params) =>
  queryString.stringify(params, { arrayFormat: "none" });
cachedAxios.defaults.paramsSerializer = (params) =>
  queryString.stringify(params, { arrayFormat: "none" });

Highcharts.setOptions({
  lang: {
    thousandsSep: ",",
  },
  chart: {
    style: {
      fontFamily: '"Manrope", sans-serif',
    },
  },
});

function AppRoot() {
  return (
    <Provider store={store}>
      <AppContextProvider>
        <ToastContainer />
        <Suspense fallback={<Loading center />}>
          <App />
        </Suspense>
      </AppContextProvider>
    </Provider>
  );
}

const container = document.getElementById("app-root");
if (!container) throw new Error("Failed to find the app-root element");
const root = createRoot(container);

root.render(<AppRoot />);
